import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor, HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { NGXLogger } from 'ngx-logger';
import {environment} from '../../../environments/environment';
import { ModalService } from '../services/modal.service';
import { DeviceService } from '../services/device.service';

/**
 * Shows an error message if a request fails.
 *
 * To prevent error messages for expected errors (e.g. 404), it is possible to configure which errors should be ignored
 * (globally in here or per request in `ScgHttpWrapper.withoutErrorInterception()`).
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  /**
   * If there is an error with one of the given status codes during a request to one of the following URLs,
   * the error is not handled in this interceptor
   *
   * ```
   * {
   *   url: environment.apiUrl + 'someendpoint',
   *   status: [404]
   * }
   * ```
   */
  private readonly ignoredUrls: {url: string, status: number[], exact?: boolean}[] = [
    // {
    //   url: environment.apiUrl + 'someendpoint',
    //   status: [404]
    // }
    {
       url: environment.apiUrl + 'action/gpsAsync',
       status: [400]
     },
    {
      url: environment.apiUrl + 'login',
      status: [403]
    },
    {
      // Nur der Login-POST selbst (nicht login/mfa/..., login/logout ...): Server startet/gestoert, die Login-Seite
      // zeigt dafuer LOGIN.ERROR_UNAVAILABLE statt des Fehlerdialogs.
      url: environment.apiUrl + 'login',
      status: [502, 503, 504],
      exact: true
    }
  ];

  constructor(
    private logger: NGXLogger,
    private modal: ModalService,
    private device: DeviceService
    ) {}

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        let statusCodesToIgnore: number[] = [];
        let ignoreAllErrors = false;

        if (request.headers.get('X-interceptor-error-status-codes-to-ignore')) {
          statusCodesToIgnore = request.headers.get('X-interceptor-error-status-codes-to-ignore')
            .split(',').map(code => Number.parseInt(code, 10));

          if (statusCodesToIgnore.length === 0) {
            ignoreAllErrors = true;
          }

          request = request.clone({
            headers: request.headers.delete('X-interceptor-error-status-codes-to-ignore')
          });
        }

        const ignore = (this.ignoreUrl(error.url ?? '', error.status ?? 0) || statusCodesToIgnore.includes(error.status))
            && !ignoreAllErrors;

        // Don't show error MessageBox for 401 response or error 0
        if (error.status === 401 || error.status === 0) {
          this.logger.info(`Got 401: ${error.status}, ${error.statusText}\n${error.message}`);
          return throwError(error);
        }

        if (!(error.error instanceof ErrorEvent) && error.url != null && !ignore) {
          // Server error
          this.logger.error(`HTTP error: ${error.status}, ${error.statusText}\n${error.message}`);
          this.modal.showErrorMessageBox({
            deviceName: this.device.activeDevice?.device.name,
            username: this.device.activeDevice?.username,
            httpCode: error.status, url: error.url,
            msg: error.message
          });
        } else if (!ignore) {
          // Client error
          this.logger.error('Error executing HTTP request', error);
          this.modal.showErrorMessageBox({
            deviceName: this.device.activeDevice?.device.name,
            username: this.device.activeDevice?.username,
            httpCode: error.status, url: error.url,
            msg: error.message
          });
        }

        return throwError(error);
      })
    );
  }

  /**
   * Tests whether the given URL together with the given status code is contained in the list of URLs that should be ignored
   */
  private ignoreUrl(url: string, statusCode: number) {
    return this.ignoredUrls.some((ignoreUrl) => {
      const urlMatches = ignoreUrl.exact === true ? url.split('?')[0] === ignoreUrl.url : url.startsWith(ignoreUrl.url);
      return urlMatches && ignoreUrl.status.includes(statusCode);
    });
  }
}


